import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  Controls,
  Background,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { graphBuilderApi, apiService } from '../../services/api';
import BuildGraphMenu from '../UserHome/BuildGraphMenu';
import NodePalette from './NodePalette';
import PropertiesPanel from './PropertiesPanel';
import LLMNode from './nodes/LLMNode';
import SNNPipelineNode from './nodes/SNNPipelineNode';
import GateNode from './nodes/GateNode';
import CaseExtractorNode from './nodes/CaseExtractorNode';
import ChainReasonerNode from './nodes/ChainReasonerNode';
import HttpNode from './nodes/HttpNode';
import PythonNode from './nodes/PythonNode';
import CrossLinkNode from './nodes/CrossLinkNode';
import { exportToProcessJSON, importFromProcessJSON, autoLayoutNodes } from './utils/processConverter';
import { validateProcess } from './utils/processValidator';
import NLGeneratorPanel from './NLGeneratorPanel';
import './ProcessBuilderPage.css';

// Define custom node types
const nodeTypes = {
  llmNode: LLMNode,
  snnPipelineNode: SNNPipelineNode,
  gateNode: GateNode,
  caseExtractorNode: CaseExtractorNode,
  chainReasonerNode: ChainReasonerNode,
  httpNode: HttpNode,
  pythonNode: PythonNode,
  crossLinkNode: CrossLinkNode,
};

function ProcessBuilderPageInner() {
  const { graphId, processId } = useParams();
  const navigate = useNavigate();
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const basePath = graphId
    ? `/user-home/graphs/build/${graphId}/processes`
    : '/user-home/process-maps';

  // ReactFlow state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  
  // Process metadata
  const [processMetadata, setProcessMetadata] = useState({
    name: '',
    description: '',
    maxParallel: 5,
    globalTimeoutSeconds: 600,
    tags: [],
    focusAreas: '',
  });
  
  // Gates (stored separately from canvas nodes)
  const [gates, setGates] = useState([]);
  
  // UI State
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedType, setSelectedType] = useState(null); // 'node', 'edge', 'gate'
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidation, setShowValidation] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showNLGenerator, setShowNLGenerator] = useState(!processId || processId === 'new');

  const [availableGraphs, setAvailableGraphs] = useState([]);
  const [availableRouters, setAvailableRouters] = useState([]);
  const [linkSummary, setLinkSummary] = useState(null);

  useEffect(() => {
    const fetchGraphs = async () => {
      try {
        const resp = await apiService.listGraphs(1, 200);
        const allGraphs = resp.graphs || resp || [];
        setAvailableGraphs(allGraphs.filter(g => g.graphType !== 'Router'));
        setAvailableRouters(allGraphs.filter(g => g.graphType === 'Router'));
      } catch (err) {
        console.error('Failed to load graphs for picker:', err);
      }
    };
    fetchGraphs();
  }, []);

  // Fetch graph link summary for this process
  useEffect(() => {
    if (!processId || processId === 'new') return;
    graphBuilderApi.getGraphLinkSummary({ process_id: processId })
      .then((summary) => setLinkSummary(summary?.nodes || null))
      .catch(() => setLinkSummary(null));
  }, [processId]);

  // Load existing process if editing
  useEffect(() => {
    if (processId && processId !== 'new') {
      loadProcess();
    }
  }, [processId]);

  const loadProcess = async () => {
    try {
      setLoading(true);
      const response = await graphBuilderApi.getProcessDefinition(processId);
      
      // Convert process definition to ReactFlow format
      const { nodes: rfNodes, edges: rfEdges, gates: processGates, metadata } = 
        importFromProcessJSON(response);
      
      setNodes(rfNodes);
      setEdges(rfEdges);
      setGates(processGates);
      setProcessMetadata(metadata);
      setIsDirty(false);

      // Fetch link summary and inject counts into node data
      if (processId && processId !== 'new') {
        try {
          const summary = await graphBuilderApi.getGraphLinkSummary({ process_id: processId });
          const nodeLinks = summary?.nodes || {};
          setLinkSummary(nodeLinks);
          setNodes((nds) =>
            nds.map((n) => ({
              ...n,
              data: {
                ...n.data,
                knowledgeLinkCount: nodeLinks[n.data?.nodeId]?.link_count || 0,
              },
            }))
          );
        } catch { /* link summary not critical */ }
      }
    } catch (err) {
      console.error('Error loading process:', err);
      setError(err.message || 'Failed to load process');
    } finally {
      setLoading(false);
    }
  };

  // Handle node connection
  const onConnect = useCallback(
    (params) => {
      const newEdge = {
        ...params,
        type: 'smoothstep',
        animated: false,
        data: {
          outputMapping: {},
          condition: '',
          weight: 1.0,
        },
      };
      setEdges((eds) => addEdge(newEdge, eds));
      setIsDirty(true);
    },
    [setEdges]
  );

  // Handle node selection
  const onNodeClick = useCallback((event, node) => {
    setSelectedElement(node);
    setSelectedType('node');
  }, []);

  // Handle edge selection
  const onEdgeClick = useCallback((event, edge) => {
    setSelectedElement(edge);
    setSelectedType('edge');
  }, []);

  // Handle pane click (deselect)
  const onPaneClick = useCallback(() => {
    setSelectedElement(null);
    setSelectedType(null);
  }, []);

  // Handle drop from palette
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow-type');
      const nodeData = JSON.parse(event.dataTransfer.getData('application/reactflow-data') || '{}');

      if (!type || !reactFlowInstance) {
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode = {
        id: `${type}_${Date.now()}`,
        type,
        position,
        data: {
          ...nodeData,
          node_id: `${type}_${Date.now()}`,
        },
      };

      setNodes((nds) => nds.concat(newNode));
      setIsDirty(true);
    },
    [reactFlowInstance, setNodes]
  );

  // Update node data from properties panel
  const handleNodeUpdate = useCallback((nodeId, newData) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: { ...node.data, ...newData },
          };
        }
        return node;
      })
    );
    setIsDirty(true);
  }, [setNodes]);

  // Keep selectedElement in sync with nodes when they change
  useEffect(() => {
    if (selectedElement && selectedType === 'node') {
      const currentNode = nodes.find(n => n.id === selectedElement.id);
      if (currentNode && currentNode !== selectedElement) {
        setSelectedElement(currentNode);
      }
    }
  }, [nodes, selectedElement, selectedType]);

  // Update edge data from properties panel
  const handleEdgeUpdate = useCallback((edgeId, newData) => {
    setEdges((eds) =>
      eds.map((edge) => {
        if (edge.id === edgeId) {
          return {
            ...edge,
            data: { ...edge.data, ...newData },
          };
        }
        return edge;
      })
    );
    setIsDirty(true);
  }, [setEdges]);

  // Update gate data
  const handleGateUpdate = useCallback((gateId, newData) => {
    setGates((gts) =>
      gts.map((gate) => {
        if (gate.gate_id === gateId) {
          return { ...gate, ...newData };
        }
        return gate;
      })
    );
    setIsDirty(true);
  }, []);

  // Add new gate
  const handleAddGate = useCallback((gateData) => {
    const newGate = {
      gate_id: `gate_${Date.now()}`,
      gate_type: gateData.gate_type || 'AND',
      input_nodes: gateData.input_nodes || [],
      output_nodes: gateData.output_nodes || [],
      threshold: gateData.threshold,
      confidence_threshold: gateData.confidence_threshold,
      low_confidence_nodes: gateData.low_confidence_nodes || [],
      description: gateData.description || '',
    };
    setGates((gts) => [...gts, newGate]);
    setIsDirty(true);
  }, []);

  // Delete gate
  const handleDeleteGate = useCallback((gateId) => {
    setGates((gts) => gts.filter((g) => g.gate_id !== gateId));
    if (selectedElement?.gate_id === gateId) {
      setSelectedElement(null);
      setSelectedType(null);
    }
    setIsDirty(true);
  }, [selectedElement]);

  // Delete selected node
  const handleDeleteNode = useCallback(() => {
    if (selectedElement && selectedType === 'node') {
      setNodes((nds) => nds.filter((n) => n.id !== selectedElement.id));
      setEdges((eds) => eds.filter(
        (e) => e.source !== selectedElement.id && e.target !== selectedElement.id
      ));
      setSelectedElement(null);
      setSelectedType(null);
      setIsDirty(true);
    }
  }, [selectedElement, selectedType, setNodes, setEdges]);

  // Delete selected edge
  const handleDeleteEdge = useCallback(() => {
    if (selectedElement && selectedType === 'edge') {
      setEdges((eds) => eds.filter((e) => e.id !== selectedElement.id));
      setSelectedElement(null);
      setSelectedType(null);
      setIsDirty(true);
    }
  }, [selectedElement, selectedType, setEdges]);

  // Validate process
  const handleValidate = useCallback(() => {
    const processJSON = exportToProcessJSON(nodes, edges, gates, processMetadata, graphId || null);
    const errors = validateProcess(processJSON);
    setValidationErrors(errors);
    setShowValidation(true);
    return errors.length === 0;
  }, [nodes, edges, gates, processMetadata, graphId]);

  // Save process
  const handleSave = async () => {
    // Validate first
    if (!handleValidate()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const processJSON = exportToProcessJSON(nodes, edges, gates, processMetadata, graphId || null);
      
      if (processId && processId !== 'new') {
        await graphBuilderApi.updateProcessDefinition(processId, processJSON);
      } else {
        const response = await graphBuilderApi.createProcessDefinition(processJSON);
        navigate(`${basePath}/${response.process_id}`, { replace: true });
      }
      
      setIsDirty(false);
    } catch (err) {
      console.error('Error saving process:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to save process');
    } finally {
      setSaving(false);
    }
  };

  // Export JSON
  const handleExport = () => {
    const processJSON = exportToProcessJSON(nodes, edges, gates, processMetadata, graphId || null);
    const blob = new Blob([JSON.stringify(processJSON, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${processMetadata.name || 'process'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Import JSON
  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const processJSON = JSON.parse(e.target.result);
        const { nodes: rfNodes, edges: rfEdges, gates: processGates, metadata } = 
          importFromProcessJSON(processJSON);
        
        setNodes(rfNodes);
        setEdges(rfEdges);
        setGates(processGates);
        setProcessMetadata(metadata);
        setIsDirty(true);
      } catch (err) {
        console.error('Error importing JSON:', err);
        setError('Invalid JSON file');
      }
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset input
  };

  // Handle AI-generated process JSON
  const handleNLGenerated = useCallback((processJSON) => {
    const { nodes: rfNodes, edges: rfEdges, gates: processGates, metadata } =
      importFromProcessJSON(processJSON);
    const layoutedNodes = autoLayoutNodes(rfNodes, rfEdges);
    setNodes(layoutedNodes);
    setEdges(rfEdges);
    setGates(processGates);
    setProcessMetadata(metadata);
    setIsDirty(true);
    setShowNLGenerator(false);
  }, [setNodes, setEdges]);

  // Navigate back
  const handleBack = () => {
    if (isDirty) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(basePath);
      }
    } else {
      navigate(basePath);
    }
  };

  // Navigate to run process
  const handleRun = () => {
    if (processId && processId !== 'new') {
      if (isDirty) {
        if (window.confirm('You have unsaved changes. Save before running?')) {
          handleSave().then(() => {
            navigate(`${basePath}/${processId}/run`);
          });
        }
      } else {
        navigate(`${basePath}/${processId}/run`);
      }
    }
  };

  // Get all node IDs for gate configuration
  const getAllNodeIds = useCallback(() => {
    return nodes.map((n) => ({
      id: n.data.node_id || n.id,
      name: n.data.name || n.data.node_id || n.id,
      type: n.type,
    }));
  }, [nodes]);

  if (loading) {
    return (
      <div className="process-builder-page">
        {graphId && <BuildGraphMenu />}
        <div className="loading-state">
          <div className="spinner"></div>
          <span>Loading process...</span>
        </div>
      </div>
    );
  }

  const isAutoGenerated = processMetadata.tags && processMetadata.tags.includes('dynamic_process');

  return (
    <div className="process-builder-page">
      {graphId && <BuildGraphMenu />}

      {isAutoGenerated && (
        <div className="auto-generated-banner">
          <span className="banner-icon">⚡</span>
          <span>This process was auto-generated from a cortex intelligence graph. You can freely edit nodes, synapses, and gates — manual edits will be preserved.</span>
        </div>
      )}

      {/* Toolbar */}
      <div className="process-builder-toolbar">
        <div className="toolbar-left">
          <input
            type="text"
            placeholder="Process Name"
            value={processMetadata.name}
            onChange={(e) => {
              setProcessMetadata((m) => ({ ...m, name: e.target.value }));
              setIsDirty(true);
            }}
            className="process-name-input"
          />
          {isDirty && <span className="unsaved-indicator">• Unsaved</span>}
        </div>
        <div className="toolbar-right">
          <button
            className="toolbar-btn ai-generate"
            onClick={() => setShowNLGenerator(true)}
            title="Generate process map from natural language"
          >
            AI Generate
          </button>
          <button 
            className="toolbar-btn validate"
            onClick={handleValidate}
            title="Validate Process"
          >
            ✓ Validate
          </button>
          <button 
            className="toolbar-btn export"
            onClick={handleExport}
            title="Export JSON"
          >
            ↓ Export
          </button>
          <label className="toolbar-btn import" title="Import JSON">
            ↑ Import
            <input 
              type="file" 
              accept=".json" 
              onChange={handleImport}
              style={{ display: 'none' }}
            />
          </label>
          <button 
            className="toolbar-btn save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : '💾 Save'}
          </button>
          {processId && processId !== 'new' && (
            <>
              <button 
                className="toolbar-btn run"
                onClick={handleRun}
                title="Run Process"
              >
                ▶ Run
              </button>
              <button
                className="toolbar-btn run"
                onClick={() => navigate(`${basePath}/${processId}/ab`)}
                title="Run Process A/B"
                style={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
              >
                A/B Test
              </button>
            </>
          )}
          <button 
            className="toolbar-btn back"
            onClick={handleBack}
          >
            ← Back
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Validation Errors */}
      {showValidation && validationErrors.length > 0 && (
        <div className="validation-errors">
          <div className="validation-header">
            <span>⚠️ Validation Errors ({validationErrors.length})</span>
            <button onClick={() => setShowValidation(false)}>×</button>
          </div>
          <ul>
            {validationErrors.map((err, idx) => (
              <li key={idx}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {/* NL Generator Panel */}
      {showNLGenerator && (
        <NLGeneratorPanel
          availableGraphs={availableGraphs}
          availableRouters={availableRouters}
          onApply={handleNLGenerated}
          hasExistingNodes={nodes.length > 0}
          onClose={() => setShowNLGenerator(false)}
        />
      )}

      {/* Main Content */}
      <div className="process-builder-content">
        {/* Node Palette */}
        <NodePalette />

        {/* ReactFlow Canvas */}
        <div className="canvas-container" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onInit={setReactFlowInstance}
            nodeTypes={nodeTypes}
            fitView
            snapToGrid
            snapGrid={[15, 15]}
            deleteKeyCode="Delete"
          >
            <Controls />
            <MiniMap 
              nodeColor={(n) => {
                if (n.type === 'llmNode') return '#3b82f6';
                if (n.type === 'snnPipelineNode') return '#10b981';
                if (n.type === 'gateNode') return '#f59e0b';
                if (n.type === 'caseExtractorNode') return '#6366f1';
                if (n.type === 'chainReasonerNode') return '#f59e0b';
                if (n.type === 'crossLinkNode') return '#0ea5e9';
                return '#6b7280';
              }}
            />
            <Background variant="dots" gap={15} size={1} />
          </ReactFlow>
        </div>

        {/* Properties Panel */}
        <PropertiesPanel
          selectedElement={selectedElement}
          selectedType={selectedType}
          processMetadata={processMetadata}
          gates={gates}
          allNodes={getAllNodeIds()}
          availableGraphs={availableGraphs}
          availableRouters={availableRouters}
          onMetadataUpdate={(updates) => {
            setProcessMetadata((m) => ({ ...m, ...updates }));
            setIsDirty(true);
          }}
          onNodeUpdate={handleNodeUpdate}
          onEdgeUpdate={handleEdgeUpdate}
          onGateUpdate={handleGateUpdate}
          onGateAdd={handleAddGate}
          onGateDelete={handleDeleteGate}
          onDeleteNode={handleDeleteNode}
          onDeleteEdge={handleDeleteEdge}
          onSelectGate={(gate) => {
            setSelectedElement(gate);
            setSelectedType('gate');
          }}
        />
      </div>
    </div>
  );
}

// Wrap with ReactFlowProvider
function ProcessBuilderPage() {
  return (
    <ReactFlowProvider>
      <ProcessBuilderPageInner />
    </ReactFlowProvider>
  );
}

export default ProcessBuilderPage;
